import { useEffect, useMemo, useState } from 'react'
import { metricsApiClient } from '../../api/http/wireApiClient'
import type { ArticleActivityEntry, ArticleNavMethodsByArticleEntry } from '../metrics/types'
import { dateToApi, nDaysAgoStr, todayStr } from '../metrics/helpers'
import type { PageArticleInlineMetrics } from './PageInlineMetricsCard'

type MetricsByArticleId = Record<string, PageArticleInlineMetrics>

export function usePageInlineMetrics(enabled: boolean, pageId: string | undefined, articleIds: string[]) {
  const [metricsByArticleId, setMetricsByArticleId] = useState<MetricsByArticleId>({})
  const [isLoading, setIsLoading] = useState(false)

  const articleIdsKey = useMemo(
    () => Array.from(new Set(articleIds.filter(Boolean))).sort().join('|'),
    [articleIds],
  )

  const uniqueArticleIds = useMemo(
    () => (articleIdsKey ? articleIdsKey.split('|') : []),
    [articleIdsKey],
  )

  useEffect(() => {
    if (!enabled || !pageId || uniqueArticleIds.length === 0) {
      setMetricsByArticleId({})
      setIsLoading(false)
      return
    }

    let cancelled = false
    setIsLoading(true)
    const params = {
      from: dateToApi(nDaysAgoStr(90), false),
      to: dateToApi(todayStr(), true),
    }

    async function load() {
      try {
        const { data: activityRows = [] } = await metricsApiClient.get<ArticleActivityEntry[]>('/metrics/article/activity', { params })
        const filteredRows = activityRows.filter(row => uniqueArticleIds.includes(row.articleId))

        const navResults = await Promise.allSettled(
          uniqueArticleIds.map(async articleId => {
            const { data } = await metricsApiClient.get<ArticleNavMethodsByArticleEntry>(`/metrics/nav-methods/${articleId}`, { params })
            return [articleId, data] as const
          }),
        )

        const navByArticleId: Record<string, ArticleNavMethodsByArticleEntry | null> = {}
        for (const result of navResults) {
          if (result.status === 'fulfilled') {
            const [articleId, data] = result.value
            navByArticleId[articleId] = data ?? null
          }
        }

        const next: MetricsByArticleId = {}
        for (const articleId of uniqueArticleIds) {
          const rows = filteredRows.filter(row => row.articleId === articleId)
          const totalTimeMs = rows.reduce((sum, row) => sum + row.timeSpentMs, 0)
          const uniqueReaders = new Set(rows.map(row => row.clientDn)).size
          const avgTimeMs = rows.length > 0 ? Math.round(totalTimeMs / rows.length) : 0
          const avgScrollDepth = rows.length > 0
            ? Math.round(rows.reduce((sum, row) => sum + row.maxScrollYIndex, 0) / rows.length)
            : 0
          const viewsFromPage = navByArticleId[articleId]?.referringPages.find(page => page.pageId === pageId)?.views ?? 0

          next[articleId] = {
            viewsFromPage,
            uniqueReaders,
            avgTimeMs,
            avgScrollDepth,
          }
        }

        if (!cancelled) {
          setMetricsByArticleId(next)
          setIsLoading(false)
        }
      } catch {
        if (!cancelled) {
          setMetricsByArticleId({})
          setIsLoading(false)
        }
      }
    }

    void load()

    return () => {
      cancelled = true
    }
  }, [enabled, pageId, articleIdsKey])

  return { metricsByArticleId, isLoading }
}